import { DropoffPricingMode } from "@redotech/congo-enums/dropoff-pricing-mode";
import { Currency } from "@redotech/money/currencies";

export const VIRTUAL_PROVIDER = "VIRTUAL" as const;
/** @deprecated ReturnBear is retired; superseded by {@link VIRTUAL_PROVIDER}. Parses stored dropoffs only. */
export const RETURN_BEAR_PROVIDER = "RETURN_BEAR" as const;
export const BLUE_YONDER_PROVIDER = "BLUE_YONDER" as const;
export const HAPPY_RETURNS_PROVIDER = "HAPPY_RETURNS" as const;

export const dropoffProviderType = [
  VIRTUAL_PROVIDER,
  RETURN_BEAR_PROVIDER,
  BLUE_YONDER_PROVIDER,
  HAPPY_RETURNS_PROVIDER,
] as const;
export type DropoffProviderType = (typeof dropoffProviderType)[number];
export const DropoffProviderType = dropoffProviderType;

const merchantFacingProviderNames: Record<DropoffProviderType, string> = {
  VIRTUAL: "Virtual",
  /** @deprecated ReturnBear is retired; no merchant-facing surface offers it. */
  RETURN_BEAR: "ReturnBear",
  BLUE_YONDER: "FedEx Easy Returns",
  HAPPY_RETURNS: "Happy Returns",
};

export function getMerchantFacingProviderName(
  provider: DropoffProviderType,
): string {
  return merchantFacingProviderNames[provider];
}

export const DEFAULT_DROPOFF_RADIUS_MILES = 50;

export const CUSTOM_DROPOFF_SHIPMENT_ID_PREFIX = "custom-dropoff/";

/**
 * Flat-rate fee (USD) when no fee is stored on the draft return. `null` means
 * the fee resolves through another path (e.g. ReturnBear's rate table).
 */
export const defaultDropoffFeeUsd = {
  VIRTUAL: 5,
  BLUE_YONDER: 6,
  /** @deprecated ReturnBear is retired; no new dropoffs quote this fee. */
  RETURN_BEAR: null,
  /** Happy Returns has no default fee; pricing is negotiated per merchant. */
  HAPPY_RETURNS: null,
} as const satisfies Record<DropoffProviderType, number | null>;

export const DEFAULT_VIRTUAL_DROPOFF_FEE_USD = defaultDropoffFeeUsd.VIRTUAL;
export const DEFAULT_BLUE_YONDER_DROPOFF_FEE_USD =
  defaultDropoffFeeUsd.BLUE_YONDER;

/** A network's standard drop-off price, when it has a flat one. */
export function defaultDropoffFee(
  provider: DropoffProviderType,
): { amount: number; currency: Currency } | undefined {
  const amount = defaultDropoffFeeUsd[provider];
  return amount === null ? undefined : { amount, currency: Currency.USD };
}

/** A team's stored pricing for one network, from wherever that network keeps it. */
export interface StoredDropoffPricing {
  mode: DropoffPricingMode;
  /** The `fixed` price. */
  amount?: number | null;
  /** Currency of the `fixed` price. */
  currency: Currency;
  /** In the standard price's currency. */
  markupAmount?: number | null;
  markupPercent?: number | null;
}

/** Networks whose price a team configures; the retired one has none. */
export type PricedDropoffProviderType = Exclude<
  DropoffProviderType,
  typeof RETURN_BEAR_PROVIDER
>;

/** The team's dropoff settings entry for one network. */
export interface StoredDropoffSettings {
  pricingMode?: DropoffPricingMode | null;
  fee?: number | null;
  feeCurrency?: Currency | null;
  markupAmount?: number | null;
  markupPercent?: number | null;
}

/** Blue Yonder keeps its pricing on the integration rather than on the team. */
export interface StoredBlueYonderDropoffPricing {
  pricingMode?: DropoffPricingMode | null;
  upcharge?: number | null;
  upchargeCurrency?: Currency | null;
  markupAmount?: number | null;
  markupPercent?: number | null;
}

function pricing(
  stored: {
    pricingMode?: DropoffPricingMode | null;
    amount?: number | null;
    currency?: Currency | null;
    markupAmount?: number | null;
    markupPercent?: number | null;
  },
  modeWhenUnset: DropoffPricingMode,
): StoredDropoffPricing {
  return {
    mode: stored.pricingMode ?? modeWhenUnset,
    amount: stored.amount,
    // Fixed prices predate the currency field, and every stored one is USD.
    //redontIgnore
    currency: stored.currency ?? Currency.USD,
    markupAmount: stored.markupAmount,
    markupPercent: stored.markupPercent,
  };
}

/**
 * Legacy Virtual fees and Blue Yonder upcharges have no mode: a positive amount
 * was the price, and zero or unset meant the standard price.
 */
function legacyModeWhenUnset(amount: number | null | undefined) {
  return amount ? DropoffPricingMode.Fixed : DropoffPricingMode.Default;
}

/** A network's stored pricing, read from wherever that network keeps it. */
export function storedDropoffPricing(
  provider: PricedDropoffProviderType,
  stored: {
    settings?: StoredDropoffSettings | null;
    blueYonderIntegration?: StoredBlueYonderDropoffPricing | null;
  },
): StoredDropoffPricing {
  switch (provider) {
    case VIRTUAL_PROVIDER: {
      const settings = stored.settings;
      return pricing(
        {
          pricingMode: settings?.pricingMode,
          amount: settings?.fee,
          currency: settings?.feeCurrency,
          markupAmount: settings?.markupAmount,
          markupPercent: settings?.markupPercent,
        },
        legacyModeWhenUnset(settings?.fee),
      );
    }
    case BLUE_YONDER_PROVIDER: {
      const integration = stored.blueYonderIntegration;
      return pricing(
        {
          pricingMode: integration?.pricingMode,
          amount: integration?.upcharge,
          currency: integration?.upchargeCurrency,
          markupAmount: integration?.markupAmount,
          markupPercent: integration?.markupPercent,
        },
        legacyModeWhenUnset(integration?.upcharge),
      );
    }
    case HAPPY_RETURNS_PROVIDER: {
      const settings = stored.settings;
      return pricing(
        {
          pricingMode: settings?.pricingMode,
          amount: settings?.fee,
          currency: settings?.feeCurrency,
          markupAmount: settings?.markupAmount,
          markupPercent: settings?.markupPercent,
        },
        // Happy Returns never stored a fee, so unset means its negotiated fee.
        DropoffPricingMode.Default,
      );
    }
  }
}
